import {
  Breadcrumbs,
  Link as MuiLink,
  Typography,
  Button,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  TextField,
  Select,
} from "@mui/material"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { useQueryClient } from "@tanstack/react-query"
import { apiFetchWeb } from "@/hooks/apiFetchWeb"
import { toast } from "sonner"
import { CrudProvider } from "@/context/crudContext"
import { DetailTable } from "@/components/Tables/DetailTable"
import { getColumns } from "@/pages/products/DetailColumns"
import OrderSummary from "@/components/Orders/OrderSummary"
import CustomerDetails from "@/components/Products/CustomerDetails"
import { isWeightable } from "@/lib/utils"
import { formatToDevanagariStyle } from "@/utilities/helper"
import { useEffect, useState } from "react"
import { apiFetch } from "@/hooks/apiFetch"
import WebsiteOrderStatusPanel from "./WebsiteOrderStatusPanel"
import ConfirmDialog from "@/components/Dialog/ConfirmDialog"
import { isAdminPortal } from "@/utilities/domain"

const WebsiteOrderDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isAccepting, setIsAccepting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [notes, setNotes] = useState("")
  const [isSavingNotes, setIsSavingNotes] = useState(false)
  const [notesError, setNotesError] = useState("")
  const [isAssigningBranch, setIsAssigningBranch] = useState(false)
  const { data: response, isLoading } = useCustomQuery(
    ["order-detail", id],
    `order/${id}`
  )
  useEffect(() => {
    if (response?.data) {
      setNotes(response.data.admin_notes ?? "")
      setNotesError("")
    }
  }, [id, response?.data?.admin_notes])
  const { data: branchResponse } = useCustomQuery(
    ["fulfillment-branches"],
    "branches/all",
    { enabled: isAdminPortal() && Boolean(response?.data) }
  )
  const branches = branchResponse?.data ?? []

  if (isLoading) return <Typography>Loading...</Typography>

  const order = response?.data
  const returnItems =
    order?.sales_returns?.flatMap((salesReturn) =>
      salesReturn.items.map((item) => ({
        ...item,
        sales_return_code: salesReturn.code,
        sales_return_date: salesReturn.date,
      }))
    ) ?? []

  const handleSaveNotes = async () => {
    setIsSavingNotes(true)
    setNotesError("")
    try {
      const result = await apiFetch(`order/${id}/notes`, {
        method: "PUT",
        body: JSON.stringify({ notes }),
      })
      queryClient.setQueryData(["order-detail", id], (previous) => ({
        ...previous,
        data: { ...previous.data, admin_notes: result.data.admin_notes },
      }))
      setNotes(result.data.admin_notes ?? "")
      toast.success("Notes saved successfully")
    } catch (error) {
      setNotesError(
        error?.error?.notes?.[0] ||
          error?.message ||
          "Failed to save notes. Please try again."
      )
    } finally {
      setIsSavingNotes(false)
    }
  }

  const handleAcceptOrder = async () => {
    setIsAccepting(true)
    try {
      await apiFetch(`order/${id}/confirm`, {
        method: "POST",
      })
      toast.success("Order confirmed successfully")
      await queryClient.invalidateQueries({
        queryKey: ["order-detail", id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["website-orders"],
      })
    } catch (error) {
      toast.error(error?.message || "Failed to confirm order")
    } finally {
      setIsAccepting(false)
    }
  }

  const handleCancelOrder = async () => {
    setIsCancelDialogOpen(false)
    setIsCancelling(true)
    try {
      await apiFetch(`order/${id}/cancel`, {
        method: "POST",
      })
      toast.success("Website order cancelled successfully")
      await queryClient.invalidateQueries({
        queryKey: ["order-detail", id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["website-orders"],
      })
    } catch (error) {
      toast.error(error?.message || "Failed to cancel website order")
    } finally {
      setIsCancelling(false)
    }
  }

  const handleStatusSaved = (fields) => {
    queryClient.setQueryData(["order-detail", id], (previous) => ({
      ...previous,
      data: { ...previous.data, ...fields },
    }))
    queryClient.invalidateQueries({ queryKey: ["website-orders"] })
  }

  const isPending = order?.status === 2 || order?.status_parsed === "Pending"
  const isAccepted = order?.status === 1 || order?.status_parsed === "Success"
  const handleFulfillmentBranchChange = async (branchId) => {
    setIsAssigningBranch(true)
    try {
      await apiFetch(`order/${id}/fulfillment-branch`, {
        method: "PUT",
        body: JSON.stringify({ branch_id: branchId }),
      })
      toast.success("Fulfillment branch updated successfully")
      await queryClient.invalidateQueries({
        queryKey: ["order-detail", id],
      })
      await queryClient.invalidateQueries({
        queryKey: ["website-orders"],
      })
    } catch (error) {
      toast.error(error?.message || "Failed to update fulfillment branch")
    } finally {
      setIsAssigningBranch(false)
    }
  }

  const returnItemColumn = [
    {
      header: "RETURN",
      accessorKey: "sales_return_code",
    },
    {
      header: "SKU",
      accessorKey: "sku",
      cell: ({ row }) => ` ${row.original.product.sku} %`,
    },
    {
      header: "CODE",
      accessorKey: "code",
      cell: ({ row }) => ` ${row.original.product?.code}`,
    },
    {
      header: "QUANTITY",
      accessorKey: "quantity",
    },
    {
      header: "PRICE",
      accessorKey: "total_price",
      cell: ({ row }) =>
        `Rs. ${formatToDevanagariStyle(row.original.total_price)}`,
    },
  ]

  return (
    <Box>
      {/* Breadcrumbs */}
      <Box
        sx={{
          marginBottom: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink
            component={Link}
            to="/pos/dashboard"
            underline="hover"
            color="inherit"
          >
            Dashboard
          </MuiLink>
          <MuiLink
            component={Link}
            to="/pos/website-orders"
            underline="hover"
            color="inherit"
          >
            Website Orders
          </MuiLink>
          <Typography color="text.primary">{order.code}</Typography>
        </Breadcrumbs>
        <Box display="flex" alignItems="center" gap={1}>
          {isPending && isAdminPortal() && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="fulfillment-branch-label">
                Fulfillment branch
              </InputLabel>
              <Select
                labelId="fulfillment-branch-label"
                label="Fulfillment branch"
                value={order?.branch_id ?? ""}
                disabled={isAssigningBranch}
                onChange={(event) =>
                  handleFulfillmentBranchChange(event.target.value)
                }
              >
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isPending && !isAdminPortal() && (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsCancelDialogOpen(true)}
                disabled={isCancelling || isAccepting}
              >
                {isCancelling ? "Cancelling..." : "Cancel Order"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAcceptOrder}
                disabled={isAccepting || isCancelling}
              >
                {isAccepting ? "Accepting..." : "Accept Order"}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <WebsiteOrderStatusPanel
        order={order}
        canEdit={isAccepted && !isAdminPortal()}
        onSaved={handleStatusSaved}
      />

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Product List Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ paddingX: 3, paddingTop: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
              Products
            </Typography>
            <CrudProvider>
              <ProductList order={order} isLoading={isLoading} />
            </CrudProvider>
          </Paper>
          {returnItems.length > 0 && (
            <Paper elevation={1} sx={{ paddingX: 3, paddingTop: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
                Return Items
              </Typography>
              <DetailTable
                label="Return Items"
                columns={returnItemColumn}
                data={returnItems}
                isLoading={false}
              />
            </Paper>
          )}
        </Grid>

        {/* Sidebar: Customer + Summary */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={3}>
            <CustomerDetails order={order} />
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
                Delivery Contact
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone number
              </Typography>
              <Typography sx={{ mb: 2 }}>
                {order.delivery_phone || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Alternative phone number
              </Typography>
              <Typography>{order.delivery_alternative_phone || "—"}</Typography>
            </Paper>
            {/* The company admin portal reviews orders; branches handle them */}
            {isAdminPortal() ? (
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Notes
                </Typography>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {order.admin_notes || "—"}
                </Typography>
              </Paper>
            ) : (
              <Paper elevation={1} sx={{ p: 3 }}>
                <TextField
                  label="Notes"
                  multiline
                  minRows={4}
                  fullWidth
                  value={notes}
                  onChange={(event) => {
                    setNotes(event.target.value)
                    setNotesError("")
                  }}
                  disabled={isSavingNotes}
                  inputProps={{ maxLength: 5000 }}
                  error={!!notesError}
                  helperText={notesError}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveNotes}
                  disabled={
                    isSavingNotes || notes === (order.admin_notes ?? "")
                  }
                  sx={{ mt: 2 }}
                >
                  {isSavingNotes ? "Saving..." : "Save Notes"}
                </Button>
              </Paper>
            )}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fulfillment branch
              </Typography>
              <Typography fontWeight={600}>
                {order.branch?.name ?? "Unassigned"}
              </Typography>
            </Paper>
            {order.split_payments.length > 0 && (
              <div>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  marginBottom={2}
                >
                  Split Payments
                </Typography>
                {order.split_payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex justify-between items-center"
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {payment.payment_method_name}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Rs. {formatToDevanagariStyle(payment.amount)}
                    </Typography>
                  </div>
                ))}
              </div>
            )}
            <OrderSummary order={order} />
          </Box>
        </Grid>
      </Grid>
      <ConfirmDialog
        open={isCancelDialogOpen}
        title="Cancel Website Order"
        description="Are you sure you want to cancel this website order?"
        confirmText="Cancel Order"
        onClose={() => setIsCancelDialogOpen(false)}
        onConfirm={handleCancelOrder}
      />
    </Box>
  )
}

function ProductList({ order, isLoading }) {
  const columns = getColumns(isWeightable)
  return (
    <DetailTable
      label="Website Orders"
      columns={columns}
      data={order.order_items}
      isLoading={isLoading}
    />
  )
}

export default WebsiteOrderDetails
